import { useEffect } from 'react';
import { useAppRouter } from '../../../hooks/useAppRouter';
import { resolveSegmentPage, normalizePathnameForSegment } from './helpers';

let lastEmittedSegmentPathname: string | null = null;

const SEGMENT_PAGE_POLL_MS = 50;
const SEGMENT_PAGE_POLL_MAX_MS = 10_000;

function emitSegmentPage(pathname: string): boolean {
  if (typeof window === 'undefined') return true;

  const templateKey = normalizePathnameForSegment(pathname);

  if (templateKey === lastEmittedSegmentPathname) return true;

  const match = resolveSegmentPage(templateKey);

  if (!match) {
    lastEmittedSegmentPathname = templateKey;
    return true;
  }

  if (typeof window.analytics?.page !== 'function') return false;

  window.analytics.page(match.category, match.pageName, {
    path_template: templateKey,
    path: window.location.pathname,
    url: window.location.href,
    title: document.title,
  });

  lastEmittedSegmentPathname = templateKey;
  return true;
}

export function useSegmentPageViews(): void {
  const router = useAppRouter();

  useEffect(() => {
    if (!router.isReady) return;

    let cancelled = false;
    const startedAt = Date.now();

    const tryEmit = (): void => {
      if (cancelled) return;

      const emitted = emitSegmentPage(router.pathname);
      const timedOut = Date.now() - startedAt > SEGMENT_PAGE_POLL_MAX_MS;

      if (!emitted && !timedOut) {
        window.setTimeout(tryEmit, SEGMENT_PAGE_POLL_MS);
      }
    };

    tryEmit();

    return () => {
      cancelled = true;
    };
  }, [router.isReady, router.pathname]);
}
